import { useState, useCallback, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import {
  DocumentSearchForm,
  DocumentsTable,
  useDocuments,
  getErrorDisplayInfo,
} from '@/features/documents';
import type { DocumentSearchRequest, DocumentSortField, SortOrder } from '@/features/documents';
import { toast } from '@/shared/utils/toast';

export const Route = createFileRoute('/_auth/profiles/applicant-documents')({
  component: ApplicantDocumentsPage,
});

/**
 * Applicant Documents Search Page
 * Search and manage applicant documentation, certifications, and supporting files
 */
function ApplicantDocumentsPage() {
  // Search state
  const [searchParams, setSearchParams] = useState<DocumentSearchRequest>({
    page: 1,
    limit: 10,
  });

  // Sort state
  const [sortBy, setSortBy] = useState<DocumentSortField | undefined>();
  const [sortOrder, setSortOrder] = useState<SortOrder>('ASC');

  // Track whether search has been initiated
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch documents with current params - only when search has been initiated
  const { data, isLoading, isError, error } = useDocuments(searchParams, hasSearched);

  // Handle search form submission
  const handleSearch = useCallback((params: DocumentSearchRequest) => {
    setSearchParams({
      ...params,
      page: 1, // Reset to first page on new search
      limit: 10,
    });
    setHasSearched(true); // Enable the query
  }, []);

  // Handle search reset
  const handleReset = useCallback(() => {
    setSearchParams({
      page: 1,
      limit: 10,
    });
    setHasSearched(false); // Disable the query and clear results
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setSearchParams((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  // Handle sort change
  const handleSort = useCallback((field: DocumentSortField) => {
    // Determine new sort order
    const newOrder = sortBy === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    
    // Update all states
    setSortBy(field);
    setSortOrder(newOrder);
    setSearchParams((prev) => ({
      ...prev,
      sort_by: field,
      order: newOrder,
      page: 1, // Reset to first page on sort change
    }));
  }, [sortBy, sortOrder]);

  // Show toast notifications for errors
  // This keeps the layout visible and shows errors as non-intrusive popups
  useEffect(() => {
    if (hasSearched && isError && error) {
      const errorInfo = getErrorDisplayInfo(error);
      
      // Show toast based on error type
      if (errorInfo.variant === 'warning') {
        toast.warning({
          title: errorInfo.title,
          description: errorInfo.message,
        });
      } else {
        toast.error({
          title: errorInfo.title,
          description: errorInfo.message,
        });
      }
    }
  }, [isError, error, hasSearched]);

  return (
    <div className="container mx-auto p-6">

      {/* Two-column layout: Sidebar + Main Content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Sidebar - Search Form */}
        <div className="lg:w-80 shrink-0">
          <DocumentSearchForm
            onSearch={handleSearch}
            onReset={handleReset}
            isLoading={isLoading}
          />
        </div>

        {/* Right Main Content - Results */}
        <div className="flex-1 min-w-0">
          {hasSearched ? (
            <div className="space-y-4">
              {/* Results Table with overflow wrapper */}
              <div className="overflow-x-auto w-full">
                <DocumentsTable
                  documents={data?.list || []}
                  isLoading={isLoading}
                  currentPage={data?.page.currentPage || 1}
                  totalPages={data?.page.totalPages || 1}
                  totalRecords={data?.page.totalRecords || 0}
                  pageSize={data?.page.limit || 10}
                  onPageChange={handlePageChange}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
              </div>

              {/* Page Info */}
              {data && data.list.length > 0 && (
                <div className="text-sm text-gray-500 text-center">
                  Page {data.page.currentPage} of {data.page.totalPages}
                </div>
              )}
            </div>
          ) : (
            /* Initial State - No search performed */
            <div className="flex items-center justify-center h-64 bg-white rounded-lg shadow-sm">
              <p className="text-gray-600 text-center uppercase tracking-wide">
                Enter information in the left panel to search applicants.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
